import { TemplateField, getFieldName } from "../../../session_templates";
import { TFunction } from "i18next";

/**
 * Build a single HTML document from template fields + their current values.
 * Each field name becomes an H2 heading, its content follows as HTML.
 * The heading uses a data-field-id attribute so we can reliably parse it back.
 */
export function buildTemplateHTML(
    fields: TemplateField[],
    values: Record<string, string>,
    t?: TFunction,
    lng?: string
): string {
    const sorted = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return sorted
        .map((f) => {
            const content = values[f.id!] || "";
            // Provide an empty paragraph so the user has somewhere to click if content is empty
            const body = content || "<p></p>";
            const displayName = (t || lng) ? getFieldName(f.name, t as TFunction, lng) : f.name;
            return `<h2 data-field-id="${f.id}">${escapeHtml(displayName)}</h2>${body}`;
        })
        .join("");
}

/**
 * Parse a unified template HTML document back into per-field values.
 * Splits on H2 headings that carry a data-field-id attribute.
 * Returns a map of fieldId → inner HTML of content that follows that heading.
 */
export function parseTemplateHTML(
    html: string,
    fields: TemplateField[]
): Record<string, string> {
    const result: Record<string, string> = {};

    // Use DOMParser if available (browser), fall back to regex for SSR safety
    if (typeof window !== "undefined" && window.DOMParser) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const body = doc.body;
        const children = Array.from(body.childNodes);

        let currentFieldId: string | null = null;
        let buffer: string[] = [];

        const flush = () => {
            if (currentFieldId !== null) {
                result[currentFieldId] = buffer.join("") || "<p></p>";
            }
        };

        for (const node of children) {
            const el = node as HTMLElement;
            const isHeading = el.nodeType === 1 && (el.tagName === "H2" || el.tagName === "H3");
            const fieldIdAttribute = el.nodeType === 1 ? el.getAttribute("data-field-id") : null;
            const headerText = el.textContent?.trim().toLowerCase();

            let matchedFieldId: string | null = fieldIdAttribute;

            // Fallback: match by name if ID is missing
            if (isHeading && !matchedFieldId && headerText) {
                const foundField = fields.find(f =>
                    f.name.toLowerCase() === headerText ||
                    // Handle common AI translations (Subjective ↔ Subjetivo, etc.)
                    // English headers → Spanish field names
                    (headerText === "subjective" && f.name.toLowerCase() === "subjetivo") ||
                    (headerText === "objective" && f.name.toLowerCase() === "objetivo") ||
                    (headerText === "assessment" && f.name.toLowerCase() === "evaluación") ||
                    (headerText === "plan" && f.name.toLowerCase() === "plan") ||
                    (headerText === "datos" && f.name.toLowerCase() === "data") ||
                    ((headerText === "situación" || headerText === "situaciones") && f.name.toLowerCase() === "situation") ||
                    (headerText === "intervención" && f.name.toLowerCase() === "intervention") ||
                    (headerText === "respuesta" && f.name.toLowerCase() === "response") ||
                    // Spanish headers → English field names (reverse mapping)
                    (headerText === "subjetivo" && f.name.toLowerCase() === "subjective") ||
                    (headerText === "objetivo" && f.name.toLowerCase() === "objective") ||
                    (headerText === "evaluación" && f.name.toLowerCase() === "assessment") ||
                    (headerText === "evaluacion" && f.name.toLowerCase() === "assessment") ||
                    (headerText === "conducta" && f.name.toLowerCase() === "behavior") ||
                    (headerText === "comportamiento" && f.name.toLowerCase() === "behavior") ||
                    (headerText === "intervención" && f.name.toLowerCase() === "intervention") ||
                    (headerText === "respuesta" && f.name.toLowerCase() === "response") ||
                    (headerText === "situación" && f.name.toLowerCase() === "situation") ||
                    (headerText === "datos" && f.name.toLowerCase() === "data") ||
                    // Accent-insensitive fallback (strip accents and compare)
                    removeAccents(f.name.toLowerCase()) === removeAccents(headerText ?? "")
                );
                if (foundField) matchedFieldId = foundField.id!;
            }

            if (isHeading && matchedFieldId) {
                flush();
                buffer = [];
                currentFieldId = matchedFieldId;
            } else {
                if (node.nodeType === 1) { // Element Node
                    buffer.push((node as HTMLElement).outerHTML);
                } else if (node.nodeType === 3) { // Text Node
                    buffer.push(node.textContent || "");
                }
            }
        }
        flush();
    }

    // Ensure every field has at least an empty entry
    fields.forEach((f) => {
        if (f.id && result[f.id] === undefined) {
            result[f.id] = "";
        }
    });

    return result;
}

function escapeHtml(str: string): string {
    return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function removeAccents(str: string): string {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}
